import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { addressesIncludeKey } from '@/_containers/inputs/address';
import { phoneNumberIncludeKey } from '@/_containers/inputs/phone-number';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import {
  type TeamMemberCreateSchema,
  type TeamMemberQuerySchema,
  teamMemberQuerySchema,
  type TeamMembersQueryAllSchema,
  type TeamMemberUpdateSchema,
} from './schemas';
import type { TeamMembersBirthdaysAllQuerySchema } from './schemas/birthdays-query-schema';
import type {
  CommissionPeriodsCreateSchema,
  CommissionPeriodsFormSchema,
  CommissionPeriodsMutationSchema,
} from './schemas/commission-periods-schema';

const includeKeys = [
  'business_card_template',
  'vector_signatured',
  'gallery',
  phoneNumberIncludeKey('emergency_contact'),
  'offer_letter',
  'handbook_acknowledgement',
  'employment_agreement',
  'parent',
  phoneNumberIncludeKey(),
  addressesIncludeKey(),
  'profile_image',
  'commission_periods.commission_plan',
];

export function getTeamMembersQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['TEAM_MEMBERS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<TeamMembersQueryAllSchema>({
        endpoint: '/api/v1/team-members',
        params: `${apiParamStr}&include=profile_image`,
      });

      return res;
    },
  };
}

export function useGetTeamMembers(apiParamStr: string) {
  return useSuspenseQuery(getTeamMembersQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['TEAM_MEMBERS'] });
};

export const useGetTeamMembersMutation: UseControlledMutation<TeamMemberQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page, ...rest }) => {
      return queryClient.fetchQuery({
        queryKey: ['TEAM_MEMBERS_ASYNC', { search, per_page, ...rest }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<TeamMembersQueryAllSchema>({
            endpoint: '/api/v1/team-members',
            params: { search, per_page, include: `profile_image,${phoneNumberIncludeKey()}`, ...(rest || {}) },
          });

          return orderBy(res?.data ?? [], ['first_name']) || [];
        },
      });
    },
  });

export function useGetTeamMember(id: string) {
  return useSuspenseQuery(getTeamMemberQuery(id));
}

export const invalidateTeamMemberQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['TEAM_MEMBER', id] });
};

export function getTeamMemberQuery(id: string) {
  return {
    queryKey: ['TEAM_MEMBER', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: TeamMemberQuerySchema }>({
        endpoint: `/api/v1/team-members/${id}?include=${includeKeys.join(',')}`,
        //  TODO: add new props when API is ready (Phase 2)
        // ,preferred_relationship`,
        responseSchema: teamMemberQuerySchema,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export function getTeamMemberQueryForCommissionDashboard(id: string) {
  return {
    queryKey: ['TEAM_MEMBER_FOR_COMMISSION_DASHBOARD', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: TeamMemberQuerySchema }>({
        endpoint: `/api/v1/team-members/${id}?include=profile_image,commission_periods.commission_plan`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, unknown, TeamMemberCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/team-members/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: TeamMemberQuerySchema }, ApiError, TeamMemberUpdateSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/team-members/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'team-members', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: TeamMemberQuerySchema }, ApiError, Partial<TeamMemberUpdateSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/team-members/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['TEAM_MEMBER_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/team-members/${id}`,
        method: 'DELETE',
      }),
  });

export function useGetTeamMembersUpcomingBirthdays(apiParamStr: string) {
  return useSuspenseQuery(getTeamMembersUpcomingBirthdaysQuery(apiParamStr));
}

export function getTeamMembersUpcomingBirthdaysQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['TEAM_MEMBERS_UPCOMING_BIRTHDAYS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<TeamMembersBirthdaysAllQuerySchema>({
        endpoint: '/api/v1/upcoming-birthdays/team-members',
        params: `${apiParamStr}`,
      });
      return res;
    },
  };
}

// commission periods APIs
export const useCommissionPeriodsBulkCreateMutation = () =>
  useMutation<CommissionPeriodsFormSchema[], ApiError, { items: CommissionPeriodsCreateSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/commission-periods/bulk-store',
        payload,
      }),
  });

export const useCommissionPeriodsBulkUpdateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, { items: CommissionPeriodsMutationSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/commission-periods/bulk-update',
        payload,
      }),
  });

export const useCommissionPeriodsBulkDeleteMutation = () =>
  useMutation<
    { data: { id: number } },
    ApiError,
    {
      items: number[];
    }
  >({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/commission-periods/bulk-delete',
        payload,
      }),
  });
